import createCache, { type EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { directionOf, type Direction, type Locale } from '@alola/i18n';
import { createContext, use, useMemo, type ReactNode } from 'react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { safePalette, type BrandOverrides, type Palette } from './brand';
import { createAppTheme } from './theme';
import { tokens } from './tokens';

const PaletteContext = createContext<Palette>(tokens);

/**
 * The validated palette in use — the deployment's brand colours, or the approved tokens. Charts and
 * branded panels read colours here rather than from `tokens`, so a brand change applies at runtime.
 */
export function usePalette(): Palette {
  return use(PaletteContext);
}

/**
 * Emotion caches per direction. In RTL, MUI's internal physical styles are mirrored by the stylis RTL
 * plugin; application code uses logical properties, which the plugin leaves untouched (I18N-004).
 */
export function createEmotionCache(direction: Direction): EmotionCache {
  return direction === 'rtl'
    ? createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] })
    : createCache({ key: 'muiltr', stylisPlugins: [prefixer] });
}

const caches: Partial<Record<Direction, EmotionCache>> = {};

function cacheFor(direction: Direction): EmotionCache {
  return (caches[direction] ??= createEmotionCache(direction));
}

/**
 * Theme, direction-aware style cache, and baseline — all derived from one `locale` value so theme
 * direction, style mirroring, and font can never disagree (I18N-003).
 *
 * `brandPrimary` is the deployment's configured brand colour (THEME-013). It is validated again here:
 * a colour that fails any contrast pair is ignored and the approved palette is used, so a browser never
 * renders an unvalidated colour whatever the server sent.
 */
export function ThemeRoot({
  locale,
  brandPrimary,
  brandPalette,
  children,
}: {
  locale: Locale;
  brandPrimary?: string | undefined;
  /** The rest of the brand palette (white-label identity), validated with the primary as a whole. */
  brandPalette?: BrandOverrides | undefined;
  children: ReactNode;
}) {
  const key = JSON.stringify(brandPalette ?? {});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by value, not by object identity
  const palette = useMemo(() => safePalette(brandPrimary, brandPalette), [brandPrimary, key]);
  const theme = useMemo(() => createAppTheme(locale, palette), [locale, palette]);
  return (
    <CacheProvider value={cacheFor(directionOf(locale))}>
      <ThemeProvider theme={theme}>
        <PaletteContext value={palette}>
          <CssBaseline />
          {children}
        </PaletteContext>
      </ThemeProvider>
    </CacheProvider>
  );
}
